<template>
  <section class="has-background-info-light" props="props">
  <div class="column">
        <o-field
        rootClass="sidebar-field"
        v-if="appStore.globalFilter['year'].includes(route.name as string)"
        label="Year"
      >
        <o-button
          class="icon-down"
          @click="setYear(-1)"
          style="z-index: 100"
          size="small"
        >
          <o-icon icon="minus" size="small" class="outline p-0"></o-icon>
        </o-button>

        <o-input v-model="appStore.thisYear" step="1" data-cy="report-year"> </o-input>

        <o-button class="icon-up" @click="setYear(1)" style="z-index: 100" size="small">
          <o-icon icon="plus" size="small" class="outline"></o-icon>
        </o-button>
      </o-field>

  </div>
    <div class="column">

      <o-field
        rootClass="sidebar-field"
        v-if="appStore.globalFilter['payCycle'].includes(route.name as string)"
        label="Pay Cycle"
      >
        <o-select
          v-model="appStore.thisPayCycle"
          @update:modelValue="thisPayCycleSelected()"
          labelSize="small"
          size="small"
        >
          <option v-for="(v, k) in ['All', ...Object.values(PAY_CYCLE)]" :key="k">
            {{ v }}
          </option>
        </o-select>
      </o-field>
      <br />

      <div v-if="appStore.globalFilter['period'].includes(route.name as string)">
        <o-field rootClass="sidebar-field" label="Period">
          <o-select
            v-model="appStore.thisPeriod"
            @update:modelValue="appStore.rightSidebar = false; thisPeriodSelected()"
            size="small"
          >
            <option
              v-for="(v, k) in periodStore.periods
                .filter(item => item.pay_cycle == appStore.thisPayCycle)
                .sort((a: Period, b: Period) => Number(dayjs(a['start_date']).format('YYYYMMDD')) - Number(dayjs(b['start_date']).format('YYYYMMDD')))"
              :value="v"
              :key="k"
              :style="v.status !== 'Open' ? 'color: #999' : ''"
            >
              {{ (v.status === 'Open' ? '🔓 ' : '🔒 ') + dayjs(v["start_date"]).format("MM/DD/YYYY") + " - " + dayjs(v["end_date"]).format("MM/DD/YYYY") }}
            </option>
          </o-select>
        </o-field>
        <span>
          {{ appStore.thisPeriod.uuid ? appStore.thisPeriod.uuid.split("-")[0] : "" }}
        </span>
      </div>

      <br />
      <div v-if="isEmployeeSheet">
        <o-field rootClass="sidebar-field" label="Employee">
          <o-select v-model="appStore.reportEmployee" expanded size="small" data-cy="report-employee">
            <option value="all">All employees (one page each)</option>
            <option v-for="e in employeeChoices" :key="e.uuid" :value="e.uuid">
              {{ e.employee_id }} — {{ e.lastname }}, {{ e.firstname }}
            </option>
          </o-select>
        </o-field>
      </div>

      <div v-if="appStore.globalFilter['company'].includes(route.name as string)">
        <o-field rootClass="sidebar-field" label="Company">
          <o-select v-model="appStore.thisCompany" size="small">
            <option v-for="(v, k) in companyStore.companies" :value="v" :key="k">
              {{ v["name"] + " (" + v["code"] + ")" }}
            </option>
          </o-select>
        </o-field>
        <span>
          {{ appStore.thisCompany.uuid ? appStore.thisCompany.uuid.split("-")[0] : "" }}
        </span>
      </div>

      <div v-if="appStore.globalFilter['account'].includes(route.name as string) || isAccountReport">
        <o-field rootClass="sidebar-field" label="Account">
          <o-select v-model="appStore.thisAccount" expanded size="small">
            <template v-if="isAccountReport">
              <option :value="{}" disabled>Select an account</option>
              <optgroup label="Earnings">
                <option v-for="account in accountStore.earningAccounts" :key="account.uuid" :value="account">
                  {{ account.account_name + " (" + account.account_number + ")" }}
                </option>
              </optgroup>
              <optgroup label="Deductions">
                <option v-for="account in accountStore.deductionAccounts" :key="account.uuid" :value="account">
                  {{ account.account_name + " (" + account.account_number + ")" }}
                </option>
              </optgroup>
            </template>
            <template v-else>
              <option
              v-for="(account, key) in route.params.type == 'deduction'
                ? accountStore.deductionAccounts
                : accountStore.earningAccounts"
              :key="key"
              :value="account"
            >
              {{ account.account_name + " (" + account.account_number + ")" }}
              </option>
            </template>
          </o-select>
        </o-field>

        <span>
          {{ appStore.thisAccount.uuid ? appStore.thisAccount.uuid.split("-")[0] : "" }}
        </span>
      </div>
    </div>
  </section>
</template>

<script setup lang="ts">
import { computed, onMounted, ref, watch } from "vue";
import { useRoute, useRouter } from "vue-router";
import { useAppStore } from "@/stores/app";
import { usePeriodStore } from "@/stores/period";
import { useCompanyStore } from "@/stores/company";
import { useEmployeeStore } from "@/stores/employee";
import { useAccountStore } from "@/stores/account";
import { usePayslipStore } from "@/stores/payslip";
import { useTransactStore } from "@/stores/transact";
import { useDailyTimeStore } from "@/stores/dailytime";

import { PAY_CYCLE, PERIOD_STATUS, type Period } from "@/stores/types";

import dayjs from "dayjs";

const route = useRoute();
const router = useRouter();

const appStore = useAppStore();
const periodStore = usePeriodStore();
const accountStore = useAccountStore();
const employeeStore = useEmployeeStore();
const companyStore = useCompanyStore();
const payslipStore = usePayslipStore();
const transactStore = useTransactStore();
const dailyTimeStore = useDailyTimeStore();

// the account report (preview/account/:uuid) picks its account here
const isAccountReport = computed(
  () => route.name == "preview" && route.params["report"] == "account"
);

// the per-employee sheets (preview/<report>/:uuid, and the accumulated totals) pick their employee here
const isEmployeeSheet = computed(
  () =>
    route.name == "preview" &&
    ["thirteenth_employee", "leave_ledger_employee", "tax_2316", "accumulated"].includes(route.params["report"] as string)
);
const employeeChoices = computed(() =>
  [...employeeStore.employees].sort((a: any, b: any) =>
    `${a.lastname} ${a.firstname}`.localeCompare(`${b.lastname} ${b.firstname}`)
  )
);

const emit = defineEmits(["importFile"]);

// Swap thisPeriod for the same-UUID object from the store so v-model identity match works
const normalizePeriod = () => {
  if (!appStore.thisPeriod?.uuid || periodStore.periods.length === 0) return;
  const match = periodStore.periods.find(p => p.uuid === appStore.thisPeriod.uuid);
  if (match) appStore.thisPeriod = match;
};

const selectCurrentPeriod = () => {
  if (appStore.thisPeriod?.uuid) return;
  const today = dayjs();
  const current = periodStore.periods.find((p) =>
    p.status === PERIOD_STATUS.Open &&
    p.pay_cycle === appStore.thisPayCycle &&
    dayjs(p.start_date) <= today &&
    today <= dayjs(p.end_date)
  );
  if (current) appStore.thisPeriod = current;
};

onMounted(() => {
  normalizePeriod();
  if (!appStore.thisPeriod?.uuid) selectCurrentPeriod();
  thisPeriodSelected();
});

// Handle periods loading after mount (fresh page load)
watch(
  () => periodStore.periods.length,
  (len, oldLen) => {
    if (len > 0) {
      normalizePeriod();
      if (oldLen === 0 && !appStore.thisPeriod?.uuid &&
          appStore.globalFilter["period"].includes(route.name as string)) {
        selectCurrentPeriod();
        thisPeriodSelected();
      }
    }
  }
);

const thisPayCycleSelected = async () => {
  localStorage.setItem("thisPayCycle", JSON.stringify(appStore.thisPayCycle));
};

const thisYearSelected = async () => {
  localStorage.setItem("thisYear", JSON.stringify(appStore.thisYear));
};

const setYear = (payload: number) => {
  appStore.thisYear += payload;
  periodStore.periodsThisYear(String(appStore.thisYear));
};

const thisPeriodSelected = async () => {
  if (route.name == "viewer") {
    localStorage.setItem("thisPeriod", JSON.stringify(appStore.thisPeriod));
    router.push({
      name: "viewer",
      params: { report: route.params["report"], uuid: appStore.thisPeriod.uuid },
      replace: true,
    });

    setTimeout(() => {
      window.location.reload();
    }, 100);
  }

  appStore.isLoading = true;
  await payslipStore.getPeriodPayslips(appStore.thisPeriod).then(() => {
    dailyTimeStore.getPeriodDailyTimes(appStore.thisPeriod);
    transactStore.getPeriodTransactions(appStore.thisPeriod);
    // payslipStore.recalcPeriodPayslips(appStore.thisPeriod)
  });
  appStore.isLoading = false;
};

// const dateRangeStr = computed(()=>(v)=>{
//   return formatDate(dayjs(v['start_date'])) + " - " + formatDate(dayjs(v['end_date'])) + " (" + v['pay_cycle'] + ") "
// })
</script>

<style>
section.has-background-info-light {
  border-radius: 0 !important;
}

.year {
  margin-left: -15px;
}
.icon-up {
  margin-left: -10px !important;
  margin-right: 0px !important;
  padding-left: 15px !important;
  padding-right: 0px !important;
}

.icon-down {
  margin-left: 0px !important;
  margin-right: -10px !important;
  padding-left: 0px !important;
  padding-right: 15px !important;
}
</style>
